<template>
	<div class="call-studio-editor">
		<b-alert :show="!!error" variant="danger" dismissible class="m-2" @dismissed="error = null">{{error?.error||error}}</b-alert>
		<b-spinner v-if="loading" class="m-3" />
		<call-device-check v-else-if="!deviceCheckReady" confirm-label="Enter the studio" @ready="onDeviceCheckReady" />
		<template v-else-if="call">
			<div class="cse-topbar">
				<b-breadcrumb class="flex-fill mb-0">
					<b-breadcrumb-item to="/">{{$title}}</b-breadcrumb-item>
					<b-breadcrumb-item to="/calls">Calls</b-breadcrumb-item>
					<b-breadcrumb-item active class="cse-current-crumb">
						<b-form-input
							v-if="editingTitle"
							v-model="titleDraft"
							size="sm"
							autofocus
							style="max-width: 260px"
							@click.stop
							@keyup.enter="saveTitle"
							@keyup.esc="editingTitle = false"
							@blur="saveTitle"
						/>
						<template v-else>
							{{call.title || 'Untitled call'}}
							<b-button variant="link" size="sm" class="text-body p-0 ml-1 cse-rename-btn" @click="startEditTitle" title="Rename call"><b-icon icon="pencil" font-scale=".85" /></b-button>
						</template>
					</b-breadcrumb-item>
				</b-breadcrumb>
				<b-button variant="secondary" size="sm" :href="guestUrl" target="_blank" rel="noopener noreferrer" title="Open the guest page"><b-icon icon="box-arrow-up-right" class="mr-1" />Guest link</b-button>
				<b-button variant="secondary" size="sm" @click="showDestinations = true"><b-icon icon="broadcast-pin" class="mr-1" />Destinations</b-button>
				<router-link v-if="liveElsewhere && !renderRunning" :to="`/calls/${liveElsewhere.id}`" class="badge badge-warning" title="Only one call can broadcast at a time - open it to stop it">"{{liveElsewhere.title || liveElsewhere.id}}" is live</router-link>
				<b-button :variant="renderRunning ? 'danger' : 'success'" size="sm" :disabled="renderBusy" @click="toggleGoLive">
					<b-spinner v-if="renderBusy" small class="mr-1" />
					<b-icon v-else :icon="renderRunning ? 'stop-circle-fill' : 'broadcast'" class="mr-1" />{{renderRunning ? 'Stop' : 'Go live'}}
				</b-button>
				<b-dropdown right size="sm" variant="secondary" class="ml-1">
					<template #button-content><b-icon icon="person-circle" class="mr-1" />{{user?.username || accountName}}</template>
					<b-dropdown-item to="/account">Account</b-dropdown-item>
					<b-dropdown-item to="/calls">All calls</b-dropdown-item>
					<b-dropdown-divider />
					<b-dropdown-item @click="logOut">Sign out</b-dropdown-item>
				</b-dropdown>
			</div>

			<div class="cse-body">
				<div class="cse-scenes" :style="{ width: `${widths.scenes}px` }">
					<div class="cse-panel-header">
						<strong>Scenes</strong>
					</div>
					<draggable v-model="scenesModel" class="cse-scene-list" ghost-class="cse-ghost">
						<div
							v-for="scene in call.scenes"
							:key="scene.id"
							class="cse-scene"
							:class="{ active: scene.id === activeSceneId }"
							@click="selectScene(scene.id)"
							@dblclick="editScene(scene)"
							@contextmenu.prevent="sceneMenu($event, scene)"
						>
							<scene-thumbnail :overlay="call" :scene="scene" />
							<div class="cse-scene-name text-truncate">{{scene.name}}</div>
						</div>
					</draggable>
					<div class="cse-scenes-footer">
						<b-button variant="primary" block class="cse-add-scene" @click="addScene"><b-icon icon="plus-lg" font-scale="1.4" /><span>Add scene</span></b-button>
					</div>
				</div>
				<div class="cse-resizer" @mousedown="startResize('scenes', $event)"></div>

				<div class="cse-layers" :style="{ width: `${widths.layers}px` }">
					<div class="cse-tabs">
						<a :class="{ active: leftTab === 'layers' }" @click="leftTab = 'layers'">Layers</a>
						<a :class="{ active: leftTab === 'chat' }" @click="openChatTab">Chat</a>
					</div>
					<div v-if="chatOpened" v-show="leftTab === 'chat'" class="cse-chat">
						<chat-log class="flex-fill" :serviceIcons="serviceIcons" />
					</div>
					<div v-show="leftTab === 'layers'" class="cse-layers-body" :class="{ 'show-properties': showProperties }">
						<div class="cse-pane cse-pane-list">
							<div class="cse-layer-actions">
								<b-button variant="secondary" size="sm" title="Undo (Ctrl+Z)" :disabled="!canUndo" @click="undo"><b-icon icon="arrow-counterclockwise" /></b-button>
								<b-button variant="secondary" size="sm" title="Redo (Ctrl+Shift+Z)" :disabled="!canRedo" @click="redo"><b-icon icon="arrow-clockwise" /></b-button>
								<b-button variant="primary" size="sm" class="ml-auto" :disabled="!activeScene" @click="showAddLayer = true"><b-icon icon="plus-lg" class="mr-1" />Add layer</b-button>
							</div>
							<draggable v-if="activeScene" v-model="layersModel" class="cse-layer-list" ghost-class="cse-ghost" handle=".cse-layer-grip">
								<div
									v-for="layer in layersModel"
									:key="layer.id"
									class="cse-layer"
									:class="{ active: layer.id === selectedId, hidden: !layer.visible }"
									:style="layer.layer_color ? { background: `${layer.layer_color}99` } : null"
									@click="selectedId = layer.id"
									@dblclick="openProperties(layer.id)"
									@contextmenu.prevent="layerMenu($event, layer)"
								>
									<div class="cse-layer-icon">
										<b-icon :icon="layerIcon(layer)" class="cse-layer-type" />
										<b-icon icon="grip-vertical" class="cse-layer-grip" />
									</div>
									<b-form-input
										v-if="renamingId === layer.id"
										v-model="layer.name"
										size="sm"
										autofocus
										@click.stop
										@dblclick.stop
										@keyup.enter="finishRename(layer)"
										@keyup.esc="finishRename(layer)"
										@blur="finishRename(layer)"
									/>
									<span v-else class="flex-fill text-truncate">{{layer.name}}</span>
									<b-icon :icon="layer.visible ? 'eye' : 'eye-slash'" class="cse-layer-toggle" :class="{ set: !layer.visible }" title="Toggle visibility" @click.stop="toggleVisible(layer)" />
									<b-icon :icon="layer.locked ? 'lock-fill' : 'unlock'" class="cse-layer-toggle" :class="{ set: layer.locked }" title="Toggle lock" @click.stop="toggleLocked(layer)" />
									<b-icon icon="three-dots-vertical" class="cse-layer-more" title="More" @click.stop="layerMoreMenu($event, layer)" />
								</div>
							</draggable>
						</div>
						<div class="cse-pane cse-pane-properties">
							<template v-if="propertiesLayer">
								<div class="cse-properties-header">
									<b-button variant="link" size="sm" class="p-0 mr-2 text-body" title="Back to layers" @click="showProperties = false"><b-icon icon="arrow-left" /></b-button>
									<strong class="text-truncate">{{propertiesLayer.name}}</strong>
								</div>
								<div class="cse-properties">
									<layer-properties-form :layer="propertiesLayer" :overlay="call" :other-layers="propertiesOtherLayers" :participants="speakerParticipants" @save="saveLayer(propertiesLayer)" @source="applySource(propertiesLayer)" />
								</div>
							</template>
						</div>
					</div>
				</div>
				<div class="cse-resizer" @mousedown="startResize('layers', $event)"></div>

				<div class="cse-editor">
					<div class="cse-scene-toolbar">
						<b-button v-for="a in sceneActions" :key="a.label" size="sm" variant="secondary" :title="a.label" @click="runSceneAction(a)"><b-icon :icon="a.icon" class="mr-1" />{{a.label}}</b-button>
					</div>
					<div class="cse-stage-wrap" ref="stageWrap">
						<overlay-stage
							:overlay="call"
							:scene="activeScene"
							:selected-id="selectedId"
							:warp-id="warpId"
							:crop-id="cropId"
							:stretch-id="stretchId"
							:preview-shared="previewShared"
							:pad="isFullscreen ? 0 : 48"
							@select="selectedId = $event"
							@geometry="applyGeometry"
							@text="applyText"
							@warp="applyWarp"
							@crop="applyCrop"
							@properties="openProperties"
							@context="canvasMenu"
						>
							<template #toolbar="{ layer }">
								<layer-toolbar
									:layer="layer"
									:warping="layer.id === warpId"
									:cropping="layer.id === cropId"
									:stretching="layer.id === stretchId"
									:can-reset="canResetTransform(layer)"
									@distort="toggleWarp(layer)"
									@crop="toggleCrop(layer)"
									@stretch="toggleStretch(layer)"
									@setting="(key, value) => setLayerSetting(layer, key, value)"
									@reset-transform="resetTransform(layer)"
									@properties="openProperties(layer.id)"
									@position="openPosition(layer.id, $event)"
									@move-up="moveLayer(layer, 1)"
									@move-down="moveLayer(layer, -1)"
									@duplicate="duplicateLayer(layer)"
									@delete="deleteLayer(layer)"
								/>
							</template>
						</overlay-stage>
					</div>
					<div class="cse-guests">
						<caller-tile
							name="You"
							:muted="micMuted"
							:stream="ownStream"
							@add="addLayer('widget', { widgetType: 'camera' })"
						/>
						<caller-tile
							v-for="d in ownDevices"
							:key="d.id"
							:name="d.label || 'Camera'"
							:stream="d.stream"
							@add="addSpeakerLayer(d.label || 'Camera', callSession.ownCallerId, d.id)"
						>
							<template #actions><b-button size="sm" variant="danger" @click="removeOwnDevice(d.id)"><b-icon icon="x-lg" class="mr-1" />Remove</b-button></template>
						</caller-tile>
						<caller-tile
							v-if="screenSharing && screenStream"
							name="Your screen"
							:stream="screenStream"
							@add="addSpeakerLayer('Your screen', callSession.ownCallerId, callSession.screenId)"
						/>
						<template v-for="c in callers">
							<caller-tile
								:key="c.callerId"
								:name="c.name || 'Guest'"
								:call-session="callSession"
								:caller-id="c.callerId"
								:on-stage="c.onStage"
								:muted="c.micMuted"
								@add="addSpeakerLayer(c.name || 'Guest', c.callerId)"
								@toggle-stage="setOnStage(c, !c.onStage)"
							>
								<template #actions>
									<b-button size="sm" variant="danger" @click="kickCaller(c)"><b-icon icon="person-x-fill" class="mr-1" />Remove</b-button>
								</template>
							</caller-tile>
							<caller-tile
								v-for="d in c.devices"
								:key="`${c.callerId}-${d.id}`"
								:name="`${c.name || 'Guest'} - ${d.label || 'Camera'}`"
								:call-session="callSession"
								:caller-id="c.callerId"
								:device-id="d.id"
								@add="addSpeakerLayer(`${c.name || 'Guest'} - ${d.label || 'Camera'}`, c.callerId, d.id)"
							/>
							<caller-tile
								v-if="c.screenId"
								:key="`${c.callerId}-screen`"
								:name="`${c.name || 'Guest'} - screen`"
								:call-session="callSession"
								:caller-id="c.callerId"
								:device-id="c.screenId"
								@add="addSpeakerLayer(`${c.name || 'Guest'} - screen`, c.callerId, c.screenId)"
							/>
						</template>
						<div class="cse-guests-actions">
							<b-dropdown size="sm" variant="secondary" dropup @show="refreshDevices">
								<template #button-content><b-icon icon="camera-video-fill" class="mr-1" />Add camera</template>
								<b-dropdown-item v-for="d in extraCameraOptions" :key="d.deviceId" @click="addOwnDevice(d)">{{d.label || `Camera ${d.deviceId.slice(0, 8)}`}}</b-dropdown-item>
								<b-dropdown-text v-if="!extraCameraOptions.length" class="text-muted small">No other cameras found</b-dropdown-text>
							</b-dropdown>
							<b-button size="sm" variant="secondary" @click="copyGuestLink"><b-icon icon="person-plus-fill" class="mr-1" />Invite guest</b-button>
						</div>
					</div>
					<div class="cse-call-controls">
						<b-button :variant="micMuted ? 'danger' : 'secondary'" @click="toggleMic" v-b-tooltip.top :title="micMuted ? 'Unmute mic' : 'Mute mic'">
							<b-icon :icon="micMuted ? 'mic-mute-fill' : 'mic-fill'" />
						</b-button>
						<b-button :variant="cameraOff ? 'danger' : 'secondary'" :disabled="cameraPending" @click="toggleCamera" v-b-tooltip.top :title="cameraOff ? 'Start camera' : 'Stop camera'">
							<b-spinner v-if="cameraPending" small />
							<b-icon v-else :icon="cameraOff ? 'camera-video-off-fill' : 'camera-video-fill'" />
						</b-button>
						<b-button :variant="speakersMuted ? 'danger' : 'secondary'" @click="toggleSpeakers" v-b-tooltip.top :title="speakersMuted ? 'Unmute speakers' : 'Mute speakers'">
							<b-icon :icon="speakersMuted ? 'volume-mute-fill' : 'volume-up-fill'" />
						</b-button>
						<b-button :variant="screenSharing ? 'primary' : 'secondary'" @click="toggleScreenShare" v-b-tooltip.top :title="screenSharing ? 'Stop sharing' : 'Share screen'">
							<b-icon icon="display" />
						</b-button>
						<b-button variant="secondary" @click="showCallSettings = true" v-b-tooltip.top title="Settings">
							<b-icon icon="gear-fill" />
						</b-button>
						<div class="flex-fill"></div>
						<b-button variant="secondary" @click="toggleFullscreen" v-b-tooltip.top :title="isFullscreen ? 'Exit full screen' : 'Full screen'">
							<b-icon :icon="isFullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'" />
						</b-button>
					</div>
				</div>
			</div>
		</template>

		<floating-panel v-if="positionPanel && selectedLayer" title="Position and size" :x="positionPanel.x" :y="positionPanel.y" :width="240" @close="positionPanel = null">
			<layer-position-form :layer="selectedLayer" :overlay="call" :media-sizes="mediaSizes" @save="saveLayer(selectedLayer)" />
		</floating-panel>

		<b-modal v-model="showAddLayer" title="Add layer" size="lg" hide-footer>
			<h6 class="text-muted">Call</h6>
			<div class="cse-add-grid mb-3">
				<b-button v-for="wt in callWidgetTypes" :key="wt.value" variant="secondary" @click="addLayerFromModal('widget', { widgetType: wt.value })"><b-icon icon="camera-video-fill" class="mr-2" />{{wt.label}}</b-button>
			</div>
			<h6 class="text-muted">Basic</h6>
			<div class="cse-add-grid mb-3">
				<b-button variant="secondary" @click="addLayerFromModal('image')"><b-icon icon="image" class="mr-2" />Image</b-button>
				<b-button variant="secondary" @click="addLayerFromModal('video')"><b-icon icon="camera-video" class="mr-2" />Video</b-button>
				<b-button variant="secondary" @click="addLayerFromModal('text')"><b-icon icon="fonts" class="mr-2" />Text</b-button>
				<b-button variant="secondary" @click="addLayerFromModal('color')"><b-icon icon="palette-fill" class="mr-2" />Color</b-button>
			</div>
			<h6 class="text-muted">Widgets</h6>
			<div class="cse-add-grid">
				<b-button v-for="wt in widgetTypes" :key="wt.value" variant="secondary" @click="addLayerFromModal('widget', { widgetType: wt.value })"><b-icon icon="grid-1x2-fill" class="mr-2" />{{wt.label}}</b-button>
			</div>
		</b-modal>

		<b-modal v-model="showDestinations" title="Destinations" hide-footer body-class="p-0" scrollable>
			<div class="cse-destinations">
				<channel-list ref="channelList" @edit="$refs.channelModal.showEdit($event.service_id, $event.token)" />
			</div>
			<div class="p-2 border-top">
				<b-button class="w-100" variant="primary" @click="$refs.channelModal.show()">Add channel</b-button>
			</div>
		</b-modal>
		<channel-modal ref="channelModal" @saved="$refs.channelList?.channelPoller?.start()" />

		<b-modal v-model="showSceneEdit" title="Edit scene" @ok="saveSceneEdit">
			<b-form-group label="Name"><b-form-input v-model="sceneDraft.name" @keyup.enter="saveSceneEdit" /></b-form-group>
		</b-modal>

		<b-modal v-model="showCallSettings" title="Settings" hide-footer>
			<b-form-group label="Camera">
				<b-form-select v-model="selectedCamera" :options="cameraOptions" @change="onCallSettingsDeviceChange"></b-form-select>
			</b-form-group>
			<b-form-group label="Microphone">
				<b-form-select v-model="selectedMic" :options="micOptions" @change="onCallSettingsDeviceChange"></b-form-select>
			</b-form-group>
			<b-form-group label="Microphone processing">
				<b-form-checkbox v-model="audioSettings.echoCancellation" class="mb-1" @change="onCallSettingsDeviceChange">Echo cancellation</b-form-checkbox>
				<b-form-checkbox v-model="audioSettings.noiseSuppression" class="mb-1" @change="onCallSettingsDeviceChange">Noise suppression</b-form-checkbox>
				<b-form-checkbox v-model="audioSettings.autoGainControl" @change="onCallSettingsDeviceChange">Auto gain control</b-form-checkbox>
			</b-form-group>
		</b-modal>

		<context-menu ref="ctx" />
	</div>
</template>

<script>
import draggable from 'vuedraggable';
import ContextMenu from '@/components/Overlay/ContextMenu.vue';
import FloatingPanel from '@/components/Overlay/FloatingPanel.vue';
import LayerToolbar from '@/components/Overlay/LayerToolbar.vue';
import LayerPositionForm from '@/components/Overlay/LayerPositionForm.vue';
import LayerPropertiesForm from '@/components/Overlay/LayerPropertiesForm.vue';
import OverlayStage from '@/components/Overlay/OverlayStage.vue';
import layerHistoryMixin from '@/components/Overlay/layerHistoryMixin';
import SceneThumbnail from '@/components/Overlay/SceneThumbnail.vue';
import CallDeviceCheck from '@/components/Calls/CallDeviceCheck.vue';
import CallerTile from '@/components/Calls/CallerTile.vue';
import ChatLog from '@/components/ChatLog.vue';
import ChannelList from '@/components/ChannelList.vue';
import ChannelModal from '@/components/Channel/Modal/ChannelModal';
import { renderSceneThumbnail } from '@/components/Overlay/renderSceneThumbnail';
import { WIDGET_TYPES, CALL_WIDGET_TYPES, ALL_WIDGET_TYPES, LAYER_COLORS, DEFAULT_PROPS, DEFAULT_SIZES, WIDGET_DEFAULT_SIZES, SCENE_TOOLBAR_ACTIONS, isMediaLayer } from '@/components/Overlay/layerTypes';
import { isChatWidgetType, createChatLayerSettings } from '@/overlay/chatSettings';
import { createSharedServices } from '@/overlay/widgetHost';
import CallSession from '@/callSession';
import { createCallAudio } from '@/callAudio';
import { createSceneClaims } from '@/callSceneClaims';
import callControlsMixin from '@/components/Calls/callControlsMixin';

// Calls have no width/height columns (there's no configurable canvas like an overlay's) - this matches the
// fixed Xvfb/ffmpeg capture size the render pipeline actually encodes at (render/RenderManager.js).
const CANVAS_WIDTH = 1280;
const CANVAS_HEIGHT = 720;

const WIDTHS_KEY = 'call-studio-editor-widths';
const CLIPBOARD_KEY = 'call-studio-editor-clipboard-layer';
const MIN_COLUMN_WIDTHS = { scenes: 140, layers: 200 };
const THUMBNAIL_INTERVAL_MS = 5000;
const RENDER_STATUS_INTERVAL_MS = 5000;

export default {
	components: { draggable, ContextMenu, FloatingPanel, LayerToolbar, LayerPositionForm, LayerPropertiesForm, OverlayStage, SceneThumbnail, CallDeviceCheck, CallerTile, ChatLog, ChannelList, ChannelModal },
	mixins: [ callControlsMixin, layerHistoryMixin ],
	data() { return {
		error: null,
		loading: false,
		call: null,
		previewShared: null,
		deviceCheckReady: false,
		ownStream: null,
		accountName: null,
		user: null,
		callSession: null,
		callers: [],
		ownDevices: [],
		leftTab: 'layers',
		chatOpened: false,
		serviceIcons: {},
		showDestinations: false,
		renderRunning: false,
		liveElsewhere: null,
		renderBusy: false,
		activeSceneId: null,
		selectedId: null,
		warpId: null,
		cropId: null,
		stretchId: null,
		mediaSizes: {},
		renamingId: null,
		editingTitle: false,
		titleDraft: '',
		showSceneEdit: false,
		sceneDraft: {},
		positionPanel: null,
		clipboard: null,
		showProperties: false,
		propertiesLayerId: null,
		showAddLayer: false,
		widths: this.loadWidths(),
		widgetTypes: WIDGET_TYPES,
		callWidgetTypes: CALL_WIDGET_TYPES,
	}},
	computed: {
		activeScene() { return this.call?.scenes.find(s => s.id === this.activeSceneId) || null; },
		selectedLayer() { return this.activeScene?.layers.find(l => l.id === this.selectedId) || null; },
		propertiesLayer() { return this.activeScene?.layers.find(l => l.id === this.propertiesLayerId) || null; },
		propertiesOtherLayers() { return (this.activeScene?.layers || []).filter(l => l.id !== this.propertiesLayerId); },
		// Other cameras this machine has that aren't already the main camera or a published extra one.
	extraCameraOptions() {
		const taken = new Set([ this.selectedCamera, ...this.ownDevices.map(d => d.deviceId) ]);
		return this.devices.filter(d => d.kind === 'videoinput' && d.deviceId && !taken.has(d.deviceId));
	},
	// Only what decides who the scene already shows (see callSceneClaims.js) - so dragging a layer around doesn't
	// re-run the grids' layout.
	claimLayers() {
		return (this.activeScene?.layers || []).map(l => ({ visible: l.visible, type: l.type, props: { widgetType: l.props?.widgetType, settings: { callerId: l.props?.settings?.callerId, deviceId: l.props?.settings?.deviceId } } }));
	},
	// Everyone (and every extra camera or screen share) a Speaker layer can be pointed at.
	speakerParticipants() {
		const list = [];
		const ownId = this.callSession?.ownCallerId;
		if(ownId) {
			list.push({ value: `${ownId}|`, text: 'You' });
			for(const d of this.ownDevices) list.push({ value: `${ownId}|${d.id}`, text: `You - ${d.label || 'camera'}` });
			if(this.screenSharing && this.callSession.screenId) list.push({ value: `${ownId}|${this.callSession.screenId}`, text: 'Your screen' });
		}
		for(const c of this.callers) {
			const name = c.name || 'Guest';
			list.push({ value: `${c.callerId}|`, text: name });
			for(const d of c.devices || []) list.push({ value: `${c.callerId}|${d.id}`, text: `${name} - ${d.label || 'camera'}` });
			if(c.screenId) list.push({ value: `${c.callerId}|${c.screenId}`, text: `${name} (screen)` });
		}
		return list;
	},
	guestUrl() { return this.call ? `${location.origin}/guest/${encodeURIComponent(this.call.id)}` : ''; },
		scenesModel: {
			get() { return this.call.scenes; },
			set(list) { this.reorderScenes(list); },
		},
		layersModel: {
			get() { return [ ...this.activeScene.layers ].sort((a, b) => b.sort_order - a.sort_order); },
			set(topFirst) { this.reorderLayers([ ...topFirst ].reverse()); },
		},
		sceneActions() {
			const seen = new Set();
			const actions = [];
			for(const layer of this.activeScene?.layers || []) {
				for(const a of SCENE_TOOLBAR_ACTIONS[layer.type === 'widget' ? layer.props.widgetType : ''] || []) {
					if(!seen.has(a.label)) { seen.add(a.label); actions.push(a); }
				}
			}
			return actions;
		},
	},
	watch: {
		selectedId(id) {
			if(this.warpId !== null && id !== this.warpId) this.warpId = null;
			if(this.cropId !== null && id !== this.cropId) this.cropId = null;
			if(this.stretchId !== null && id !== this.stretchId) this.stretchId = null;
		},
		'selectedLayer.props.src': {
			immediate: true,
			handler(src) {
				if(src && isMediaLayer(this.selectedLayer)) this.loadMediaSize(src, this.selectedLayer.type === 'video');
			},
		},
		claimLayers: {
			immediate: true,
			handler(layers) { this.sceneClaims.update(layers); },
		},
		activeSceneId(id, previousId) {
			const previous = this.call?.scenes.find(s => s.id === previousId);
			if(previous) this.refreshThumbnail(previous);
			this.selectedId = null;
			this.showProperties = false;
			if(this.activeScene) this.ensureHistory(this.activeScene);
		},
		selectedLayer(layer) {
			if(!layer) {
				this.positionPanel = null;
				this.showProperties = false;
			} else if(this.showProperties) {
				this.propertiesLayerId = layer.id;
			}
		},
	},
	// The claimLayers watcher is immediate, so it runs before created().
	beforeCreate() {
		this.sceneClaims = createSceneClaims();
	},
	created() {
		this._thumbSigs = {};
		this._mediaSizePromises = {};
		try { this.clipboard = JSON.parse(localStorage.getItem(CLIPBOARD_KEY)); } catch(e) { /* ignore */ }
	},
	async mounted() {
		window.addEventListener('keydown', this.onKeyDown);
		await this.loadCall();
		this.refreshRenderStatus();
		this.renderTimer = setInterval(this.refreshRenderStatus, RENDER_STATUS_INTERVAL_MS);
	},
	beforeDestroy() {
		clearInterval(this.thumbTimer);
		clearInterval(this.renderTimer);
		this.unsubscribeCallers?.();
		this.callAudio?.destroy();
		if(this.activeScene) this.refreshThumbnail(this.activeScene);
		window.removeEventListener('keydown', this.onKeyDown);
		this.callSession?.leave();
		this.screenStream?.getTracks().forEach(t => t.stop());
		this.ownStream?.getTracks().forEach(t => t.stop());
	},
	methods: {
		loadWidths() {
			let widths = { scenes: 200, layers: 240 };
			try { widths = { ...widths, ...JSON.parse(localStorage.getItem(WIDTHS_KEY)) }; } catch(e) { /* defaults */ }
			for(const [ which, min ] of Object.entries(MIN_COLUMN_WIDTHS)) widths[which] = Math.max(min, widths[which]);
			return widths;
		},
		startResize(which, e) {
			const startX = e.clientX;
			const startWidth = this.widths[which];
			const onMove = ev => { this.widths[which] = Math.max(MIN_COLUMN_WIDTHS[which], Math.min(520, startWidth + ev.clientX - startX)); };
			const onUp = () => {
				document.removeEventListener('mousemove', onMove);
				document.removeEventListener('mouseup', onUp);
				document.body.style.userSelect = '';
				try { localStorage.setItem(WIDTHS_KEY, JSON.stringify(this.widths)); } catch(err) { /* ignore */ }
			};
			document.body.style.userSelect = 'none';
			document.addEventListener('mousemove', onMove);
			document.addEventListener('mouseup', onUp);
		},
		async loadCall() {
			this.loading = true;
			try {
				// /scenes (authed) has the layers and widgetHash; /info (public) has the OME connection
				// details every call page needs regardless of ownership - both are cheap, load in parallel.
				const [ call, info, userData ] = await Promise.all([
					this.$api.get(`call/${this.$route.params.id}/scenes`),
					this.$api.get(`call/${this.$route.params.id}/info`),
					this.$api.get('userData').catch(() => null),
				]);
				this.call = { ...call, width: CANVAS_WIDTH, height: CANVAS_HEIGHT, webRtcSettings: info.webRtcSettings };
				this.user = userData;
				this.accountName = userData?.username || 'Host';
				this.activeSceneId = this.call.active_scene_id || this.call.scenes[0]?.id || null;
				this.maybeEnterCall();
				this.startThumbnails();
			} catch(e) {
				this.error = e;
			} finally {
				this.loading = false;
			}
		},
		// The device check (camera/mic permission, matching evmux) runs in parallel with loading the call's
		// scenes, so the editor is ready the instant both finish - whichever comes last.
		onDeviceCheckReady({ stream, cameraOn, micOn, cameraDeviceId, micDeviceId, audioSettings }) {
			this.ownStream = stream;
			this.micMuted = !micOn;
			this.cameraOff = !cameraOn;
			this.selectedCamera = cameraDeviceId;
			this.selectedMic = micDeviceId;
			if(audioSettings) this.audioSettings = audioSettings;
			this.deviceCheckReady = true;
			this.maybeEnterCall();
			navigator.mediaDevices.enumerateDevices().then(d => { this.devices = d; }).catch(() => {});
		},
		// Joins the call (publishing the device check's stream under the host's own caller row) and sets
		// up the widget preview services - including callContext, so the camera/speaker/dynamic-grid
		// mounters render real live video instead of placeholders (see widgetMounters/camera.js etc).
		// Needs both the call's info and the device check's local stream, whichever finishes loading last.
		async maybeEnterCall() {
			if(this.callSession || !this.call || !this.deviceCheckReady) return;
			this.callSession = new CallSession({ api: this.$api, callId: this.call.id, webRtcSettings: this.call.webRtcSettings });
			try {
				await this.callSession.join({ name: this.accountName, stream: this.ownStream, isHost: true });
			} catch(e) {
				console.error('Could not join own call', e);
				this.error = e;
			}
			this.unsubscribeCallers = this.callSession.onCallers(callers => { this.callers = callers; });
			this.callAudio = createCallAudio({ session: this.callSession });
			this.callSession.setMicMuted(this.micMuted);
			// Live - the camera/speaker/dynamic-grid mounters that called this earlier still get
			// the mic/camera/settings control bar's later toggles and device changes.
			const ownStreamHook = cb => {
				this.ownStreamListeners.push(cb);
				cb(this.ownStream);
				return () => { this.ownStreamListeners = this.ownStreamListeners.filter(l => l !== cb); };
			};
			this.previewShared = createSharedServices({
				hash: this.call.widgetHash,
				callContext: {
					callId: this.call.id,
					onOwnStream: ownStreamHook,
					// The studio editor's own viewer is always the call owner, so "my own stream" and
					// "the owner's stream" are the same hook here - only the guest page (and later the
					// render page) need to actually go fetch the owner's stream from someone else.
					onOwnerStream: ownStreamHook,
					ownCallerId: () => this.callSession.ownCallerId,
					ownName: () => this.accountName,
					ownMicMuted: () => this.micMuted,
					onOwnScreen: cb => this.callSession.onOwnScreen(cb),
					onSceneClaims: this.sceneClaims.subscribe,
					openReceiver: (callerId, deviceId) => this.callSession.openReceiver(callerId, deviceId),
					onCallers: cb => this.callSession.onCallers(cb),
				},
			});
		},

		// ── Call controls ──
		// Mic/camera/screen share/settings/full-screen come from callControlsMixin (shared with the guest page).

		// ── Guests & cameras ──
		addSpeakerLayer(name, callerId, deviceId = null) {
			return this.addLayer('widget', { widgetType: 'speaker', name, settings: { callerId, deviceId } });
		},
		async setOnStage(caller, onStage) {
			await this.run(this.$api.post(`call/${this.call.id}/caller/${caller.callerId}/onStage`, { onStage }));
			caller.onStage = onStage;
		},
		async kickCaller(caller) {
			if(!await this.confirmDelete(`Remove ${caller.name || 'this guest'} from the call?`)) return;
			await this.run(this.$api.delete(`call/${this.call.id}/caller/${caller.callerId}`));
		},
		copyGuestLink() {
			this.$util.copyToClipboard(this.guestUrl);
			this.$bvToast.toast('Guest link copied', { title: 'Success', variant: 'success', solid: true });
		},
		refreshDevices() {
			navigator.mediaDevices.enumerateDevices().then(d => { this.devices = d; }).catch(() => {});
		},
		// A second camera, published as its own stream next to the main one so it can be put in the scene
		// like any other participant (see CallSession.startDevice).
		async addOwnDevice(device) {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: device.deviceId } }, audio: false });
				const label = device.label || 'Camera';
				const id = await this.callSession.startDevice(stream, label, device.deviceId);
				this.ownDevices.push({ id, label, stream, deviceId: device.deviceId });
			} catch(e) {
				this.error = e;
			}
		},
		removeOwnDevice(id) {
			const device = this.ownDevices.find(d => d.id === id);
			this.callSession.stopDevice(id);
			device?.stream.getTracks().forEach(t => t.stop());
			this.ownDevices = this.ownDevices.filter(d => d.id !== id);
		},

		// ── Go live ──
		async refreshRenderStatus() {
			try {
				const status = await this.$api.get(`call/${this.call.id}/render/status`);
				const running = !!status.running;
				// It was live and now isn't, and we didn't stop it: say so instead of the button quietly flipping back.
				if(this.renderRunning && !running && !this.renderBusy) this.error = status.failed || 'The broadcast stopped on its own. Only one of your calls can broadcast at a time - if another one went live on the same stream it can knock this one off.';
				this.renderRunning = running;
				this.liveElsewhere = status.liveElsewhere || null;
			} catch(e) { /* no render service configured - the button will say so when pressed */ }
		},
		async toggleGoLive() {
			if(this.renderRunning && !await this.$bvModal.msgBoxConfirm('Stop the broadcast?', { okVariant: 'danger', okTitle: 'Stop', size: 'sm', centered: true })) return;
			this.renderBusy = true;
			try {
				const res = await this.$api.post(`call/${this.call.id}/render/${this.renderRunning ? 'stop' : 'start'}`);
				this.renderRunning = !!res.running;
			} catch(e) {
				this.error = e;
			} finally {
				this.renderBusy = false;
			}
		},

		// ── Chat tab & account ──
		async openChatTab() {
			this.leftTab = 'chat';
			if(this.chatOpened) return;
			this.chatOpened = true;
			try {
				const services = await this.$api.get('services');
				this.serviceIcons = Object.fromEntries(services.filter(s => s.icon_url).map(s => [ s.id, s.icon_url ]));
			} catch(e) { /* the log works without icons */ }
		},
		logOut() {
			delete localStorage.username;
			delete localStorage.authToken;
			this.$auth.setToken(null);
		},

		// ── Scene thumbnails ──
		thumbnailSignature(scene) {
			return JSON.stringify([ this.snapshotLayers(scene), CANVAS_WIDTH, CANVAS_HEIGHT ]);
		},
		startThumbnails() {
			for(const scene of this.call.scenes) {
				if(scene.thumbnail) this._thumbSigs[scene.id] = this.thumbnailSignature(scene);
			}
			this.refreshThumbnails();
			this.thumbTimer = setInterval(this.refreshThumbnails, THUMBNAIL_INTERVAL_MS);
		},
		async refreshThumbnails() {
			for(const scene of this.call?.scenes || []) await this.refreshThumbnail(scene);
		},
		async refreshThumbnail(scene) {
			const sig = this.thumbnailSignature(scene);
			if(this._thumbSigs[scene.id] === sig) return;
			this._thumbSigs[scene.id] = sig;
			try {
				const image = await renderSceneThumbnail(this.call, scene);
				if(image === scene.thumbnail) return;
				this.$set(scene, 'thumbnail', image);
				await this.$api.post(`call/${this.call.id}/scenes/${scene.id}/thumbnail`, { image });
			} catch(e) {
				console.error('Could not update scene thumbnail', e);
			}
		},
		run(promise) {
			return promise.catch(e => { this.error = e; });
		},
		async confirmDelete(message) {
			return this.$bvModal.msgBoxConfirm(message, { okVariant: 'danger', okTitle: 'Delete', size: 'sm', centered: true });
		},

		// ── Call ──
		startEditTitle() {
			this.titleDraft = this.call.title || '';
			this.editingTitle = true;
		},
		async saveTitle() {
			if(!this.editingTitle) return;
			this.editingTitle = false;
			if(!this.titleDraft || this.titleDraft === this.call.title) return;
			if(!await this.run(this.$api.patch(`call/${this.call.id}`, { title: this.titleDraft }))) return;
			this.call.title = this.titleDraft;
		},

		// ── Scenes ──
		selectScene(id) {
			this.activeSceneId = id;
			this.run(this.$api.post(`call/${this.call.id}/scenes/${id}/activate`));
		},
		async addScene() {
			const name = `Scene ${this.call.scenes.length + 1}`;
			const res = await this.run(this.$api.post(`call/${this.call.id}/scenes`, { name }));
			if(!res) return;
			this.call.scenes.push({ id: res.id, name, thumbnail: null, layers: [] });
			this.selectScene(res.id);
		},
		editScene(scene) {
			this.sceneDraft = { id: scene.id, name: scene.name };
			this.showSceneEdit = true;
		},
		async saveSceneEdit() {
			const scene = this.call.scenes.find(s => s.id === this.sceneDraft.id);
			if(!scene || !this.sceneDraft.name) return;
			this.showSceneEdit = false;
			const ok = await this.run(this.$api.patch(`call/${this.call.id}/scenes/${scene.id}`, { name: this.sceneDraft.name }));
			if(!ok) return;
			scene.name = this.sceneDraft.name;
		},
		async duplicateScene(scene) {
			const res = await this.run(this.$api.post(`call/${this.call.id}/scenes/${scene.id}/duplicate`));
			if(!res) return;
			const fresh = await this.run(this.$api.get(`call/${this.call.id}/scenes`));
			const created = fresh?.scenes.find(s => s.id === res.id);
			if(!created) return;
			this.call.scenes.push(created);
			this.selectScene(created.id);
		},
		async deleteScene(scene) {
			if(!await this.confirmDelete(`Delete scene "${scene.name}"?`)) return;
			if(!await this.run(this.$api.delete(`call/${this.call.id}/scenes/${scene.id}`))) return;
			this.call.scenes = this.call.scenes.filter(s => s.id !== scene.id);
			if(this.activeSceneId === scene.id) this.activeSceneId = this.call.scenes[0]?.id || null;
		},
		async reorderScenes(list) {
			this.call.scenes = list;
			await this.run(this.$api.post(`call/${this.call.id}/scenes/reorder`, { orderedSceneIds: list.map(s => s.id) }));
		},
		sceneMenu(e, scene) {
			this.$refs.ctx.open(e, [
				{ label: 'Edit', icon: 'pencil', action: () => this.editScene(scene) },
				{ label: 'Duplicate', icon: 'files', action: () => this.duplicateScene(scene) },
				{ separator: true },
				{ label: 'Paste layer', icon: 'clipboard-check', disabled: !this.clipboard, action: () => this.pasteLayer(scene) },
				{ separator: true },
				{ label: 'Delete', icon: 'trash', danger: true, action: () => this.deleteScene(scene) },
			]);
		},

		// ── Layers ──
		layerIcon(layer) {
			if(layer.type === 'widget' && layer.props.widgetType === 'camera') return 'camera-video-fill';
			if(layer.type === 'widget' && layer.props.widgetType === 'speaker') return 'person-video';
			if(layer.type === 'widget' && layer.props.widgetType === 'camera-grid') return 'grid-3x3-gap-fill';
			return { image: 'image', video: 'camera-video', text: 'fonts', color: 'palette-fill', widget: 'grid-1x2-fill' }[layer.type] || 'square';
		},
		widgetLabel(value) {
			return ALL_WIDGET_TYPES.find(w => w.value === value)?.label || value;
		},
		addLayerFromModal(type, opts) {
			this.showAddLayer = false;
			return this.addLayer(type, opts);
		},
		async addLayer(type, opts = {}) {
			const scene = this.activeScene;
			if(!scene) return;
			const isCallWidget = type === 'widget' && CALL_WIDGET_TYPES.some(w => w.value === opts.widgetType);
			const [ w, h ] = (type === 'widget' && WIDGET_DEFAULT_SIZES[opts.widgetType]) || DEFAULT_SIZES[type];
			const props = DEFAULT_PROPS[type](opts.widgetType);
			if(opts.settings) props.settings = { ...props.settings, ...opts.settings };
			if(type === 'widget' && !isCallWidget) {
				// Each account-level widget layer starts from the account's current settings for that widget type
				// (both chat layer types start from the account's chat widget). Camera/speaker/dynamic-grid have
				// no such account-level settings - they keep the defaults layerTypes.js just gave them above.
				const account = await this.$api.get(`widgetSettings/${isChatWidgetType(opts.widgetType) ? 'chat' : opts.widgetType}`).catch(() => ({})) || {};
				props.settings = isChatWidgetType(opts.widgetType) ? createChatLayerSettings(account) : account;
			}
			const point = opts.point;
			const data = {
				type,
				name: opts.name || (type === 'widget' ? this.widgetLabel(opts.widgetType) : type[0].toUpperCase() + type.slice(1)),
				visible: true,
				locked: false,
				x: Math.round(point ? Math.max(0, Math.min(CANVAS_WIDTH - w, point[0] - w / 2)) : (CANVAS_WIDTH - w) / 2),
				y: Math.round(point ? Math.max(0, Math.min(CANVAS_HEIGHT - h, point[1] - h / 2)) : (CANVAS_HEIGHT - h) / 2),
				w, h,
				rotation: 0,
				props,
			};
			const res = await this.run(this.$api.post(`call/${this.call.id}/scenes/${scene.id}/layers`, data));
			if(!res) return;
			scene.layers.push({ id: res.id, scene_id: scene.id, layer_color: null, sort_order: Math.max(-1, ...scene.layers.map(l => l.sort_order)) + 1, ...data });
			this.selectedId = res.id;
			this.commitHistory();
			if(type === 'image' || type === 'video') this.pickLayerFile(res.id);
		},
		async pickLayerFile(id) {
			this.openProperties(id);
			await this.$nextTick();
			this.$el.querySelector('.cse-properties input[type=file]')?.click();
		},
		saveLayer(layer) {
			this.commitHistory();
			return this.run(this.$api.patch(`call/${this.call.id}/scenes/${this.activeScene.id}/layers/${layer.id}`, {
				name: layer.name,
				layerColor: layer.layer_color,
				visible: layer.visible,
				locked: layer.locked,
				x: layer.x, y: layer.y, w: layer.w, h: layer.h,
				rotation: layer.rotation,
				props: layer.props,
			}));
		},
		applyGeometry(id, geometry) {
			const layer = this.activeScene.layers.find(l => l.id === id);
			if(!layer) return;
			Object.assign(layer, geometry);
			this.saveLayer(layer);
		},
		toggleWarp(layer) {
			this.warpId = this.warpId === layer.id ? null : layer.id;
			if(this.warpId !== null) { this.cropId = null; this.stretchId = null; }
		},
		toggleCrop(layer) {
			this.cropId = this.cropId === layer.id ? null : layer.id;
			if(this.cropId !== null) { this.warpId = null; this.stretchId = null; }
		},
		toggleStretch(layer) {
			this.stretchId = this.stretchId === layer.id ? null : layer.id;
			if(this.stretchId !== null) { this.cropId = null; this.warpId = null; }
		},
		loadMediaSize(src, isVideo) {
			return this._mediaSizePromises[src] ||= new Promise(resolve => {
				const el = document.createElement(isVideo ? 'video' : 'img');
				const done = size => {
					if(size) this.$set(this.mediaSizes, src, size);
					else delete this._mediaSizePromises[src];
					resolve(size);
				};
				if(isVideo) {
					el.preload = 'metadata';
					el.onloadedmetadata = () => done(el.videoWidth ? [ el.videoWidth, el.videoHeight ] : null);
				} else {
					el.onload = () => done(el.naturalWidth ? [ el.naturalWidth, el.naturalHeight ] : null);
				}
				el.onerror = () => done(null);
				el.src = src;
			});
		},
		async applySource(layer) {
			const size = layer.props.src ? await this.loadMediaSize(layer.props.src, layer.type === 'video') : null;
			if(size) {
				const k = Math.min(1, CANVAS_WIDTH / size[0], CANVAS_HEIGHT / size[1]);
				const w = Math.max(1, Math.round(size[0] * k));
				const h = Math.max(1, Math.round(size[1] * k));
				const cx = layer.x + layer.w / 2;
				const cy = layer.y + layer.h / 2;
				layer.w = w;
				layer.h = h;
				layer.x = Math.round(cx - w / 2);
				layer.y = Math.round(cy - h / 2);
			}
			this.saveLayer(layer);
		},
		canResetTransform(layer) {
			if(layer.rotation || layer.props?.warp) return true;
			if(!isMediaLayer(layer) || !layer.props?.src) return false;
			const size = this.mediaSizes[layer.props.src];
			return !size || size[0] !== layer.w || size[1] !== layer.h;
		},
		async resetTransform(layer) {
			this.warpId = null;
			this.cropId = null;
			layer.rotation = 0;
			this.$delete(layer.props, 'warp');
			this.$delete(layer.props, 'crop');
			const size = isMediaLayer(layer) && layer.props.src ? await this.loadMediaSize(layer.props.src, layer.type === 'video') : null;
			if(size) {
				const cx = layer.x + layer.w / 2;
				const cy = layer.y + layer.h / 2;
				layer.w = size[0];
				layer.h = size[1];
				layer.x = Math.round(cx - size[0] / 2);
				layer.y = Math.round(cy - size[1] / 2);
			}
			this.saveLayer(layer);
		},
		applyWarp(id, warp) {
			const layer = this.activeScene.layers.find(l => l.id === id);
			if(!layer) return;
			if(warp) this.$set(layer.props, 'warp', warp);
			else this.$delete(layer.props, 'warp');
			this.saveLayer(layer);
		},
		applyCrop(id, { x, y, w, h, crop }) {
			const layer = this.activeScene.layers.find(l => l.id === id);
			if(!layer) return;
			Object.assign(layer, { x, y, w, h });
			if(crop) this.$set(layer.props, 'crop', crop);
			else this.$delete(layer.props, 'crop');
			this.saveLayer(layer);
		},
		applyText(id, text) {
			const layer = this.activeScene.layers.find(l => l.id === id);
			if(!layer) return;
			layer.props.text = text;
			this.saveLayer(layer);
		},
		// One of the widget's own settings, from a toolbar button.
		setLayerSetting(layer, key, value) {
			this.$set(layer.props.settings, key, value);
			this.saveLayer(layer);
		},
		toggleVisible(layer) {
			layer.visible = !layer.visible;
			this.saveLayer(layer);
		},
		toggleLocked(layer) {
			layer.locked = !layer.locked;
			this.saveLayer(layer);
		},
		setLayerColor(layer, color) {
			layer.layer_color = color;
			this.saveLayer(layer);
		},
		startRename(layer) {
			this.selectedId = layer.id;
			this.renamingId = layer.id;
		},
		finishRename(layer) {
			if(this.renamingId !== layer.id) return;
			this.renamingId = null;
			if(!layer.name) layer.name = 'Layer';
			this.saveLayer(layer);
		},
		async duplicateLayer(layer) {
			const res = await this.run(this.$api.post(`call/${this.call.id}/scenes/${this.activeScene.id}/layers/${layer.id}/duplicate`));
			if(!res) return;
			const copy = JSON.parse(JSON.stringify(layer));
			this.activeScene.layers.push({ ...copy, id: res.id, name: `${layer.name} copy`, x: layer.x + 20, y: layer.y + 20, sort_order: Math.max(...this.activeScene.layers.map(l => l.sort_order)) + 1 });
			this.selectedId = res.id;
			this.commitHistory();
		},
		copyLayer(layer) {
			this.clipboard = {
				sourceSceneId: this.activeScene.id,
				type: layer.type,
				name: layer.name,
				layer_color: layer.layer_color,
				visible: layer.visible,
				locked: layer.locked,
				x: layer.x, y: layer.y, w: layer.w, h: layer.h,
				rotation: layer.rotation,
				props: JSON.parse(JSON.stringify(layer.props)),
			};
			try { localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(this.clipboard)); } catch(e) { /* ignore */ }
			this.$bvToast.toast(`Copied "${layer.name}"`, { title: 'Success', variant: 'success', solid: true });
		},
		async pasteLayer(targetScene = this.activeScene, point) {
			const c = this.clipboard;
			if(!c || !targetScene) return;
			const sameScene = c.sourceSceneId === targetScene.id;
			const x = point ? Math.round(point[0] - c.w / 2) : (sameScene ? c.x + 20 : c.x);
			const y = point ? Math.round(point[1] - c.h / 2) : (sameScene ? c.y + 20 : c.y);
			const props = JSON.parse(JSON.stringify(c.props));
			const res = await this.run(this.$api.post(`call/${this.call.id}/scenes/${targetScene.id}/layers`, {
				type: c.type, name: c.name, layerColor: c.layer_color, visible: c.visible, locked: c.locked,
				x, y, w: c.w, h: c.h, rotation: c.rotation, props,
			}));
			if(!res) return;
			targetScene.layers.push({
				id: res.id, scene_id: targetScene.id, type: c.type, name: c.name, layer_color: c.layer_color,
				visible: c.visible, locked: c.locked, x, y, w: c.w, h: c.h, rotation: c.rotation, props,
				sort_order: Math.max(-1, ...targetScene.layers.map(l => l.sort_order)) + 1,
			});
			if(targetScene.id === this.activeSceneId) this.selectedId = res.id;
			this.commitHistory(targetScene);
		},
		async deleteLayer(layer) {
			if(!await this.confirmDelete(`Delete layer "${layer.name}"?`)) return;
			if(!await this.run(this.$api.delete(`call/${this.call.id}/scenes/${this.activeScene.id}/layers/${layer.id}`))) return;
			this.activeScene.layers = this.activeScene.layers.filter(l => l.id !== layer.id);
			if(this.selectedId === layer.id) this.selectedId = null;
			this.commitHistory();
		},
		moveLayer(layer, direction) {
			const asc = [ ...this.activeScene.layers ].sort((a, b) => a.sort_order - b.sort_order);
			const i = asc.findIndex(l => l.id === layer.id);
			const j = i + direction;
			if(j < 0 || j >= asc.length) return;
			[ asc[i], asc[j] ] = [ asc[j], asc[i] ];
			this.reorderLayers(asc);
		},
		moveLayerTo(layer, front) {
			const asc = [ ...this.activeScene.layers ].sort((a, b) => a.sort_order - b.sort_order).filter(l => l.id !== layer.id);
			if(front) asc.push(layer);
			else asc.unshift(layer);
			this.reorderLayers(asc);
		},
		async reorderLayers(ascending) {
			ascending.forEach((l, i) => { l.sort_order = i; });
			this.commitHistory();
			await this.run(this.$api.post(`call/${this.call.id}/scenes/${this.activeScene.id}/layers/reorder`, { orderedLayerIds: ascending.map(l => l.id) }));
		},

		// The path of a scene's layers, for the shared undo/redo (layerHistoryMixin).
		layersApiBase(scene) {
			return `call/${this.call.id}/scenes/${scene.id}/layers`;
		},

		// ── Panels & menus ──
		openProperties(id) {
			this.selectedId = id;
			this.propertiesLayerId = id;
			this.showProperties = true;
		},
		openPosition(id, e) {
			this.selectedId = id;
			const toolbar = e.currentTarget.closest('.overlay-layer-toolbar').getBoundingClientRect();
			this.positionPanel = { x: toolbar.right + 10, y: toolbar.top };
		},
		runSceneAction(action) {
			this.run(action.run(this.$api));
		},
		layerMenuItems(layer, e) {
			const asc = [ ...this.activeScene.layers ].sort((a, b) => a.sort_order - b.sort_order);
			const index = asc.findIndex(l => l.id === layer.id);
			return [
				{ label: 'Properties', icon: 'gear', action: () => this.openProperties(layer.id) },
				{ label: 'Position and size', icon: 'bounding-box', action: () => { this.selectedId = layer.id; this.positionPanel = { x: e.clientX, y: e.clientY }; } },
				isMediaLayer(layer) ? { label: 'Crop', icon: 'crop', action: () => { this.selectedId = layer.id; this.toggleCrop(layer); } } : null,
				{ label: 'Reset transform', icon: 'bootstrap-reboot', disabled: !this.canResetTransform(layer), action: () => this.resetTransform(layer) },
				{ label: 'Rename', icon: 'pencil', action: () => this.startRename(layer) },
				{ swatches: LAYER_COLORS, value: layer.layer_color, onPick: c => this.setLayerColor(layer, c) },
				{ separator: true },
				{ label: layer.visible ? 'Hide' : 'Show', icon: layer.visible ? 'eye-slash' : 'eye', action: () => this.toggleVisible(layer) },
				{ label: layer.locked ? 'Unlock' : 'Lock', icon: layer.locked ? 'unlock' : 'lock', action: () => this.toggleLocked(layer) },
				{ separator: true },
				{ label: 'Bring to front', icon: 'chevron-double-up', disabled: index === asc.length - 1, action: () => this.moveLayerTo(layer, true) },
				{ label: 'Move up', icon: 'arrow-up', disabled: index === asc.length - 1, action: () => this.moveLayer(layer, 1) },
				{ label: 'Move down', icon: 'arrow-down', disabled: index === 0, action: () => this.moveLayer(layer, -1) },
				{ label: 'Send to back', icon: 'chevron-double-down', disabled: index === 0, action: () => this.moveLayerTo(layer, false) },
				{ separator: true },
				{ label: 'Duplicate', icon: 'files', action: () => this.duplicateLayer(layer) },
				{ label: 'Copy', icon: 'clipboard', action: () => this.copyLayer(layer) },
				{ label: 'Delete', icon: 'trash', danger: true, action: () => this.deleteLayer(layer) },
			];
		},
		layerMoreMenu(e, layer) {
			const rect = e.currentTarget.getBoundingClientRect();
			const at = { clientX: rect.left, clientY: rect.bottom + 4, preventDefault() {} };
			this.selectedId = layer.id;
			this.$refs.ctx.open(at, this.layerMenuItems(layer, at));
		},
		layerMenu(e, layer) {
			this.selectedId = layer.id;
			this.$refs.ctx.open(e, this.layerMenuItems(layer, e));
		},
		canvasMenu(e, { layerId, hits, point }) {
			const layer = layerId && this.activeScene.layers.find(l => l.id === layerId);
			const items = [];
			if(layer) {
				if(hits.length > 1) {
					items.push({ label: 'Select layer', icon: 'layers', children: hits.map(h => ({ label: h.name, icon: this.layerIcon(h), action: () => { this.selectedId = h.id; } })) });
					items.push({ separator: true });
				}
				items.push(...this.layerMenuItems(layer, e));
			}
			if(this.activeScene) {
				items.push({ label: 'Paste', icon: 'clipboard-check', disabled: !this.clipboard, action: () => this.pasteLayer(this.activeScene, point) });
				if(layer) items.push({ separator: true });
				items.push({
					label: 'Add call layer',
					icon: 'camera-video-fill',
					children: this.callWidgetTypes.map(wt => ({ label: wt.label, action: () => this.addLayer('widget', { widgetType: wt.value, point }) })),
				});
				items.push({
					label: 'Add layer',
					icon: 'plus-lg',
					children: [
						{ label: 'Image', icon: 'image', action: () => this.addLayer('image', { point }) },
						{ label: 'Video', icon: 'camera-video', action: () => this.addLayer('video', { point }) },
						{ label: 'Text', icon: 'fonts', action: () => this.addLayer('text', { point }) },
						{ label: 'Color', icon: 'palette-fill', action: () => this.addLayer('color', { point }) },
					],
				});
				items.push({
					label: 'Add widget',
					icon: 'grid-1x2-fill',
					children: this.widgetTypes.map(wt => ({ label: wt.label, action: () => this.addLayer('widget', { widgetType: wt.value, point }) })),
				});
			}
			this.$refs.ctx.open(e, items);
		},
		onKeyDown(e) {
			if(/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable) return;
			if(document.querySelector('.modal.show')) return;
			const layer = this.selectedLayer;
			if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); if(e.shiftKey) this.redo(); else this.undo(); return; }
			if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); this.redo(); return; }
			if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') { if(layer) { e.preventDefault(); this.copyLayer(layer); } return; }
			if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') { if(this.clipboard && this.activeScene) { e.preventDefault(); this.pasteLayer(); } return; }
			if(e.key === 'Escape') { this.selectedId = null; return; }
			if(!layer) return;
			if(e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); this.deleteLayer(layer); return; }
			const step = e.shiftKey ? 10 : 1;
			const delta = { ArrowLeft: [ -step, 0 ], ArrowRight: [ step, 0 ], ArrowUp: [ 0, -step ], ArrowDown: [ 0, step ] }[e.key];
			if(!delta || layer.locked) return;
			e.preventDefault();
			layer.x += delta[0];
			layer.y += delta[1];
			clearTimeout(this.nudgeTimer);
			this.nudgeTimer = setTimeout(() => this.saveLayer(layer), 300);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '../plugins/variable.scss';

.call-studio-editor {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}

.cse-topbar {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	padding: .4rem .75rem;
	border-bottom: 1px solid $border-color;
	gap: .25rem;

	// Sits directly in the topbar strip rather than as its own padded/boxed segment.
	::v-deep .breadcrumb {
		background: transparent;
		padding: 0;
		margin: 0;
		font-size: 1rem;
		flex-wrap: nowrap;
	}

	::v-deep .breadcrumb-item {
		display: flex;
		align-items: center;
		white-space: nowrap;
	}
}

.cse-current-crumb {
	// The rename button only shows up while hovering the current call's name.
	.cse-rename-btn {
		opacity: 0;
		transition: opacity .1s;
	}

	&:hover .cse-rename-btn {
		opacity: 1;
	}
}

.cse-body {
	flex: 1 1 auto;
	display: flex;
	min-height: 0;
}

.cse-scenes,
.cse-layers {
	flex: 0 0 auto;
	display: flex;
	flex-direction: column;
	min-height: 0;
	background: $gray-200;
}

.cse-layers { min-width: 200px; }

.cse-resizer {
	flex: 0 0 5px;
	cursor: col-resize;
	background: $border-color;

	&:hover { background: $green; }
}

.cse-panel-header,
.cse-tabs {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: .4rem .6rem;
	border-bottom: 1px solid $border-color;
}

.cse-tabs {
	gap: 1rem;
	padding-bottom: 0;

	a {
		cursor: pointer;
		padding-bottom: .4rem;
		border-bottom: 2px solid transparent;
		color: $text-muted;
		text-decoration: none;

		&.active { color: #fff; border-bottom-color: $green; }
	}
}

.cse-scene-list,
.cse-layer-list {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
}

.cse-scene-list { padding: .5rem; }

.cse-add-scene {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: .25rem;
	padding: .9rem .5rem;
	border-radius: .6rem;
	font-size: .8rem;
	font-weight: 600;
	line-height: 1.1;
	white-space: nowrap;
}

.cse-scenes-footer {
	flex: 0 0 auto;
	padding: .6rem;
	border-top: 1px solid $border-color;
}

.cse-scene {
	margin-bottom: .6rem;
	padding: 4px;
	border: 2px solid transparent;
	border-radius: .35rem;
	cursor: pointer;

	&:hover { background: $gray-400; }
	&.active { border-color: $green; background: $gray-400; }
}

.cse-scene-name {
	padding-top: .2rem;
	font-size: .8rem;
}

.cse-layer-list { padding: .5rem; }

.cse-layer-actions {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	gap: .35rem;
	padding: .5rem;
	border-bottom: 1px solid $border-color;
}

.cse-add-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
	gap: .5rem;

	.btn { text-align: left; }
}

.cse-layers-body {
	position: relative;
	flex: 1 1 auto;
	min-height: 0;
	overflow: hidden;
}

.cse-pane {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	background: $gray-200;
	transition: transform .25s ease, opacity .25s ease;
}

.cse-pane-list { transform: translateX(0); }
.cse-pane-properties { transform: translateX(100%); opacity: 0; visibility: hidden; transition: transform .25s ease, opacity .25s ease, visibility 0s .25s; }

.show-properties {
	.cse-pane-list { transform: translateX(-100%); opacity: 0; }
	.cse-pane-properties { transform: translateX(0); opacity: 1; visibility: visible; transition: transform .25s ease, opacity .25s ease, visibility 0s; }
}

.cse-properties-header {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	padding: .5rem .6rem;
	border-bottom: 1px solid $border-color;
}

.cse-properties {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	padding: .6rem;
}

.cse-layer {
	display: flex;
	align-items: center;
	margin-bottom: .35rem;
	padding: .5rem .6rem;
	border-radius: .4rem;
	background: rgba(255, 255, 255, .05);
	cursor: pointer;

	&:hover { background-color: $gray-400; }
	&.active { box-shadow: inset 0 0 0 2px $green; }
	&.hidden { opacity: .55; }
}

.cse-layer-icon {
	position: relative;
	flex: 0 0 auto;
	width: 18px;
	height: 18px;
	margin-right: .55rem;

	.cse-layer-type,
	.cse-layer-grip {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		transition: opacity .1s;
	}

	.cse-layer-grip { opacity: 0; cursor: grab; }
}

.cse-layer:hover .cse-layer-icon {
	.cse-layer-type { opacity: 0; }
	.cse-layer-grip { opacity: .85; }
}

.cse-layer-toggle,
.cse-layer-more {
	flex: 0 0 auto;
	margin-left: .5rem;
	cursor: pointer;
	visibility: hidden;
	opacity: .8;

	&:hover { opacity: 1; }
}

.cse-layer-toggle.set { visibility: visible; }
.cse-layer:hover .cse-layer-toggle,
.cse-layer:hover .cse-layer-more { visibility: visible; }

.cse-ghost { opacity: .4; }

.cse-editor {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	background: $body-bg;
}

.cse-scene-toolbar {
	flex: 0 0 auto;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: .35rem;
	min-height: 2.75rem;
	padding: .4rem .6rem;
}

.cse-stage-wrap {
	flex: 1 1 auto;
	min-height: 0;

	&:fullscreen {
		background: #000;
	}
}

.cse-chat {
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	flex-direction: column;
}

.cse-guests {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	gap: .5rem;
	padding: .5rem .75rem;
	border-top: 1px solid $border-color;
	overflow-x: auto;
}

.cse-guests-actions {
	flex: 0 0 auto;
	display: flex;
	flex-direction: column;
	gap: .35rem;
	margin-left: auto;
}

.cse-destinations {
	min-height: 200px;
}

.cse-call-controls {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	gap: 6px;
	padding: .5rem .75rem;
	border-top: 1px solid $border-color;
}
</style>
